import { useState, useEffect } from "react";
import {
	LOAN_PURPOSE_BUSINESS_VEHICLE,
	LOAN_PURPOSE_EQUIPMENT,
	LOAN_PURPOSE_TRUCK,
	LOAN_PURPOSE_WORKING_CAPITAL,
} from "@/lib/constants";
import { BriefcaseIcon, CarIcon, TractorIcon, TruckTrailerIcon } from "@phosphor-icons/react";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { RadioGroupField } from "@/components/ui/radioGroupField";
import clsx from "clsx";

export const BusinessLoanDetails = () => {
	const { formValues, setFormValue } = useFormDataStore();
	const {
		selectedIndex,
		enabledFields,
		enabledBusinessFields,
		setEnabledBusinessFields,
		setFormErrors,
		setValidateFormDetails,
		formContainer,
	} = useFormStateStore();

	const [isNavDivSmall, setIsNavDivSmall] = useState(false);

	// For form container
	useEffect(() => {
		const limiter = selectedIndex === 0 ? 350 : 460;

		setIsNavDivSmall(formContainer < limiter);
	}, [selectedIndex, formContainer]);

	const { loanPurpose, purchaseStage, assetAge } = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		loanPurpose: false,
		purchaseStage: false,
	});

	// Checks whether any of the fields have errors. If it does, zustand's formError is set to true
	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormErrors(hasError);
	}, [errors, setFormErrors]);

	// Function to validate all fields
	const validateForm = () => {
		const newErrors = {
			loanPurpose: enabledFields.loanPurpose?.length == 0 ? false : !loanPurpose,
			purchaseStage:
				formValues.loanPurpose === LOAN_PURPOSE_WORKING_CAPITAL
					? false
					: enabledBusinessFields.purchaseStage?.length === 0
						? false
						: !purchaseStage,
		};
		setErrors(newErrors);

		// If any error exists, set formError to true
		const hasError = Object.values(newErrors).some((error) => error);
		setFormErrors(hasError);

		return !hasError; // Returns true if validation passes
	};

	// Store the validation function in Zustand so QuickForm can call it
	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [loanPurpose, purchaseStage, assetAge]);

	// Reset formValues where it's not enabled.
	useEffect(() => {
		if (formValues.loanPurpose) {
			setEnabledBusinessFields(formValues.loanPurpose);
		}
	}, [formValues.loanPurpose, enabledBusinessFields, setFormValue, setErrors]);

	return (
		<div className="jF-space-y-4 jF-m-2 jF-pb-4">
			<RadioGroupField
				id="loanPurpose"
				label="Loan Purpose"
				value={loanPurpose || ""}
				options={enabledFields.loanPurpose.map((item) => ({
					id: item.id,
					value: item.value,
					label: item.value,
					icon:
						item.value === LOAN_PURPOSE_BUSINESS_VEHICLE ? (
							<CarIcon size={24} weight="duotone" />
						) : item.value == LOAN_PURPOSE_EQUIPMENT ? (
							<TractorIcon size={24} weight="duotone" />
						) : item.value == LOAN_PURPOSE_TRUCK ? (
							<TruckTrailerIcon size={24} weight="duotone" />
						) : item.value == LOAN_PURPOSE_WORKING_CAPITAL ? (
							<BriefcaseIcon size={24} weight="duotone" />
						) : null,
				}))}
				onChange={(value) => {
					setFormValue("loanPurpose", value);
					// Reset form values
					setFormValue("abnStatus", "");
					setErrors((prevErrors) => ({
						...prevErrors,
						loanPurpose: !value,
						purchaseStage:
							value === LOAN_PURPOSE_WORKING_CAPITAL
								? false
								: prevErrors.purchaseStage,
					}));
					setEnabledBusinessFields(value);
				}}
				required={true}
				error={errors.loanPurpose ? "This field is required" : ""}
				disabled={false}
				className={""}
				gridCols={2}
				showIcons={true}
				disabledOptions={[]}
				navRef={isNavDivSmall}
			/>

			{/* Business Vehicle Finance, Equipment Finance, Truck Finance */}
			{formValues.loanPurpose && enabledBusinessFields.purchaseStage?.length > 1 && (
				<>
					{/* 1. Purchase Stage */}
					<RadioGroupField
						id="purchaseStage"
						label="Have you identified the asset that you intend to acquire?"
						value={purchaseStage || ""}
						options={enabledBusinessFields.purchaseStage.map((item) => ({
							id: item.value,
							value: item.value,
							label: item.value,
							icon: null,
						}))}
						onChange={(value) => {
							setFormValue("purchaseStage", value);
							setErrors((prevErrors) => ({
								...prevErrors,
								purchaseStage: !value, // Clear error when a selection is made
							}));
						}}
						required={true}
						error={errors.purchaseStage ? "This field is required" : ""}
						disabled={false}
						className={""}
						gridCols={3}
						showIcons={false}
						disabledOptions={[]}
						navRef={isNavDivSmall}
					/>
				</>
			)}
		</div>
	);
};
